// Quotes: the page with its views (List, Pipeline, Plan offers), the tiles and the list.
import { QUOTE_STATUS_WORDS, type QuoteRow, type QuoteStats, type QuoteStatus } from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { useQuotes, useQuoteStats } from "../../api/money";
import { useSession } from "../../app/session";
import { DataTable } from "../../components/DataTable";
import { money, moneyExact, shortDate, trend } from "../../components/format";
import { Icon } from "../../components/Icon";
import { Button, Card, Chip, EmptyState, LoadError, Page, StatusLabel } from "../../components/parts";
import { SettingsButton } from "../settings/Feature";
import { DAY, localDay, QuotesView, QuoteTag } from "./common";
import { NewLead, PipelineBoard } from "./Pipeline";

const VIEW_KEY = "steward.quotes.view";

/** The view this browser last left Quotes on. List until one is picked. */
function readView(): QuotesView {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "pipeline" || v === "offers" ? v : "list";
  } catch {
    return "list";
  }
}

function saveView(v: QuotesView) {
  try {
    localStorage.setItem(VIEW_KEY, v);
  } catch {
    // Kept in the address until the page closes.
  }
}

type StatusPick = QuoteStatus | "all";

/**
 * Quotes in views (owner, 2026-11-03), picked at the top like the Schedule's: List (the top cards, then every quote;
 * the Overview card's counts pick the list's status) and Pipeline (leads by stage, each one a quote away), on its own.
 * Plan offers (owner, 2026-11-04, cleaning only) lists the offers to start a plan; they are not quotes, so List, its
 * cards and Pipeline leave them out. The pick stays in ?view= and in this browser for next time.
 */
export function QuotesPage({ view: asked }: { view: QuotesView | undefined }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [status, setStatus] = useState<StatusPick>("all");
  const leads = can("lead.read");
  const offers = company.trade === "cleaning";
  const views = (
    [
      ["list", "List", "list"],
      ["pipeline", "Pipeline", "pipeline"],
      ["offers", "Plan offers", "repeat"],
    ] as const
  ).filter(([v]) => (v === "pipeline" ? leads : v === "offers" ? offers : true));
  const wanted = asked ?? readView();
  const view: QuotesView = views.some(([v]) => v === wanted) ? wanted : "list";
  const pick = (v: QuotesView) => {
    saveView(v);
    void navigate({ to: "/quotes", search: { view: v }, replace: true });
  };
  return (
    <Page
      title="Quotes"
      fill={view === "pipeline"}
      moreActions={<SettingsButton to="/quotes/settings">Quote settings</SettingsButton>}
      primaryAction={
        can("quote.update") ? (
          <Button icon="plus" onClick={() => void navigate({ to: "/quotes/new", search: {} })}>
            New quote
          </Button>
        ) : null
      }
      secondaryAction={
        can("quote.update") && company.trade === "cleaning" ? (
          <Button type="secondary" onClick={() => void navigate({ to: "/proposals/new" })}>
            New proposal
          </Button>
        ) : null
      }
    >
      {views.length > 1 ? (
        <div className="segmented quotes-views" role="tablist" aria-label="View">
          {views.map(([v, label, icon]) => (
            <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => pick(v)}>
              <Icon name={icon} size={16} />
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {view === "pipeline" ? (
        <div className="quotes-board">
          <Card
            title="Pipeline"
            action={
              can("lead.update") ? (
                <Button type="secondary" size="small" icon="plus" onClick={() => setAdding(true)}>
                  New lead
                </Button>
              ) : null
            }
          >
            <PipelineBoard />
          </Card>
        </div>
      ) : view === "offers" ? (
        <div className="stack">
          <QuotesList companyId={company.id} status={status} setStatus={setStatus} offers />
        </div>
      ) : (
        <>
          <QuoteTiles companyId={company.id} status={status} onStatus={setStatus} />
          <div className="stack">
            <QuotesList companyId={company.id} status={status} setStatus={setStatus} />
          </div>
        </>
      )}
      <NewLead open={adding} onClose={() => setAdding(false)} />
    </Page>
  );
}

/**
 * The top cards (owner asked which are best, 2026-10-29). Jobber's four, which the old Steward copied: Overview (what needs
 * you now, by status, each one filters the list), Conversion rate, Sent and Converted for the past 30 days, each next to
 * the 30 days before so a slow month shows.
 */
function QuoteTiles({
  companyId,
  status,
  onStatus,
}: {
  companyId: string;
  status: StatusPick;
  onStatus: (s: StatusPick) => void;
}) {
  const stats = useQuoteStats(companyId);
  const s: QuoteStats | undefined = stats.data;
  const wait = <span className="skeleton inline" />;
  const sent = s ? trend(s.sent.count, s.sentBefore.count) : null;
  const won = s ? trend(s.converted.count, s.convertedBefore.count) : null;
  const rate = s?.rate != null && s.rateBefore !== null ? trend(s.rate, s.rateBefore, { points: true }) : null;
  return (
    <div className="tiles">
      <div className="tile">
        <span className="label">Overview</span>
        <div className="tile-counts">
          {(["draft", "sent", "approved", "expired"] as const).map((k) => (
            <Fragment key={k}>
              <button type="button" aria-pressed={status === k} onClick={() => onStatus(status === k ? "all" : k)}>
                <QuoteTag status={k} />
              </button>
              <span className="num">{s ? s.counts[k] : "-"}</span>
            </Fragment>
          ))}
        </div>
      </div>
      <div className="tile">
        <span className="label">Conversion rate</span>
        <span className="value">{s ? (s.rate === null ? "-" : `${String(s.rate)}%`) : wait}</span>
        <span className="hint">Past 30 days, sent quotes approved</span>
        {rate ? <span className={`hint ${rate.cls}`}>{rate.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">Sent</span>
        <span className="value">{s ? s.sent.count : wait}</span>
        <span className="hint">Past 30 days{s ? `, ${money(s.sent.cents)}` : ""}</span>
        {sent ? <span className={`hint ${sent.cls}`}>{sent.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">Converted</span>
        <span className="value">{s ? s.converted.count : wait}</span>
        <span className="hint">Past 30 days{s ? `, ${money(s.converted.cents)}` : ""}</span>
        {won ? <span className={`hint ${won.cls}`}>{won.text}</span> : null}
      </div>
    </div>
  );
}

const RANGES = [
  ["all", "Any time"],
  ["30", "Past 30 days"],
  ["90", "Past 90 days"],
  ["365", "Past year"],
] as const;

type Range = (typeof RANGES)[number][0];

function QuotesList({
  companyId,
  status,
  setStatus,
  offers = false,
}: {
  companyId: string;
  status: StatusPick;
  setStatus: (s: StatusPick) => void;
  /** The plan offers instead of the quotes. */
  offers?: boolean;
}) {
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>("all");
  const list = useQuotes(companyId, status, true, offers ? "only" : undefined);
  const since = range === "all" ? 0 : Date.now() - Number(range) * DAY;
  const rows = (list.data?.quotes ?? []).filter((q) => Date.parse(q.createdAt) >= since);
  return (
    <DataTable<QuoteRow>
      title={offers ? "Plan offers" : "All quotes"}
      rows={rows}
      rowKey={(q) => q.id}
      loading={list.isFetching}
      error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
      onRowClick={(q) => void navigate({ to: "/quotes/$id", params: { id: q.id } })}
      search={(q) => [q.name, q.title, q.address, `#${String(q.number)}`].join(" ")}
      tools={
        <>
          <div className="chips">
            {(offers
              ? (["all", "sent", "converted", "declined", "expired"] as const)
              : (["all", "draft", "sent", "approved", "converted", "declined", "expired"] as const)
            ).map((s) => (
              <Chip key={s} pressed={status === s} onClick={() => setStatus(s)}>
                {s === "all" ? "All" : QUOTE_STATUS_WORDS[s]}
              </Chip>
            ))}
          </div>
          <div className="chips">
            {RANGES.map(([k, label]) => (
              <Chip key={k} pressed={range === k} onClick={() => setRange(k)}>
                {label}
              </Chip>
            ))}
          </div>
        </>
      }
      empty={
        offers ? (
          <EmptyState icon="quotes" title="No plan offers here">
            A plan offer is the link in the text that asks a one-time client to come back every week, every 2 weeks or monthly.
            They pick a day and book it on their phone.
          </EmptyState>
        ) : (
          <EmptyState icon="quotes" title="No quotes here">
            A quote shows the client the work and the price. They approve it with their name on their phone.
          </EmptyState>
        )
      }
      columns={[
        {
          key: "name",
          header: "Client",
          sort: (q) => q.name.toLowerCase(),
          render: (q) => (
            <div className="name-cell">
              <b>{q.name}</b>
              <span className="muted">{q.title ?? "Quote"}</span>
            </div>
          ),
        },
        {
          key: "number",
          header: offers ? "Number" : "Quote number",
          sort: (q) => q.number,
          render: (q) => <span className="num">#{q.number}</span>,
        },
        { key: "property", header: "Property", render: (q) => q.address ?? <span className="muted">Not given</span> },
        {
          key: "created",
          header: "Created",
          sort: (q) => q.createdAt,
          render: (q) => shortDate(localDay(q.createdAt)),
        },
        {
          key: "status",
          header: "Status",
          sort: (q) => q.status,
          render: (q) => (
            <span className="row">
              <QuoteTag status={q.status} />
              {q.viewed && q.status === "sent" ? <StatusLabel status="informative">Viewed</StatusLabel> : null}
            </span>
          ),
        },
        {
          key: "total",
          header: "Total",
          align: "right",
          sort: (q) => q.totalCents,
          render: (q) => <span className="num">{moneyExact(q.totalCents)}</span>,
        },
      ]}
    />
  );
}

// The builder ----------------------------------------------------------------------------------------------------------
